import React, { useState, useEffect } from "react";
import { Edit3, AlertTriangle, Lock, ShieldCheck } from "lucide-react";
import { Modal } from "./Modal";
import { IssueNumberTypeahead } from "./IssueNumberTypeahead";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const DISCIPLINES = ["Dimensional", "Pressure", "Torque", "Temperature", "Electronics", "Hydraulics", "Mass", "Flow"];

/**
 * EditLineModal — admin/manager edits identity & spec on an existing job line.
 * Locked once a certificate is minted or status is paid/closed; a manager can
 * still override via the "Override lock" toggle (`force=true`).
 */
export function EditLineModal({ item, onClose, onSaved }) {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || user?.role === "admin";

  const locked = !!item.certificate_number || ["paid", "closed"].includes(item.status);
  const [override, setOverride] = useState(false);
  const [techs, setTechs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    description: item.description || "",
    manufacturer: item.manufacturer || "",
    serial_number: item.serial_number || "",
    asset_number: item.asset_number || "",
    part_number: item.part_number || "",
    issue_number: item.issue_number || "",
    discipline: item.discipline || "Dimensional",
    location: item.location || "",
    calibration_period_days: item.calibration_period_days || "",
    cal_method: item.cal_method || "traceable",
    priority: item.priority || "normal",
    technician_id: item.technician_id || "",
    remarks: item.remarks || "",
    notes: item.notes || "",
    reserved_certificate_number: item.reserved_certificate_number || "",
    group_item: !!item.group_item,
    group_number: item.group_number || "",
  });

  useEffect(() => {
    api.get("/employees", { params: { role: "technician" } })
      .then((r) => setTechs(r.data)).catch(() => {});
  }, []);

  const set = (patch) => setForm((s) => ({ ...s, ...patch }));
  const disabled = locked && !(override && isManager);

  // Identity/spec fields — mirrors backend's _LINE_EDIT_FIELDS lock set.
  // FIELD_DEFAULTS mirrors the fallback used when the form state above was
  // initialised, so the diff below compares like-for-like (a field whose
  // backend value is null but which the form defaulted to e.g. "Dimensional"
  // must NOT be treated as "changed" just because the user never touched it).
  const IDENTITY_FIELDS = ["description", "manufacturer", "serial_number", "asset_number",
    "part_number", "issue_number", "discipline", "location",
    "calibration_period_days", "cal_method", "notes"];
  const FIELD_DEFAULTS = { discipline: "Dimensional", cal_method: "traceable" };

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        priority: form.priority,
        technician_id: form.technician_id || null,
        remarks: form.remarks,
        group_item: form.group_item,
        group_number: form.group_number || null,
      };
      // Only include an identity/spec field if it actually changed — so a
      // locked line can still be saved for non-identity edits (group
      // number, remarks, technician, cert-number correction) WITHOUT
      // needing the manager "Override lock" toggle, since those aren't
      // traceability-sensitive fields.
      for (const k of IDENTITY_FIELDS) {
        const orig = item[k] || FIELD_DEFAULTS[k] || "";
        const cur = form[k];
        if (String(cur) !== String(orig)) {
          body[k] = k === "calibration_period_days" ? (cur ? parseInt(cur) : null) : cur;
        }
      }
      // Only send the CR-number correction if a manager actually changed it —
      // avoids tripping the manager-only guard on unrelated saves.
      if (isManager && form.reserved_certificate_number !== (item.reserved_certificate_number || "")) {
        body.reserved_certificate_number = form.reserved_certificate_number;
      }
      if (locked && override && isManager) body.force = true;
      await api.patch(`/job-items/${item.id}`, body);
      toast.success("Line updated — audit log written");
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Edit3}
      title="Edit equipment line"
      subtitle={`${item.description || ""} · ${item.serial_number ? "SN " + item.serial_number : item.issue_number ? "Issue " + item.issue_number : "—"}`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn gold" onClick={save} disabled={busy}
          data-testid="edit-line-save">
          {busy ? "Saving…" : "Save changes"}
        </button>
      </>}>

      {locked && (
        <div className="card pad" data-testid="edit-line-locked"
          style={{ marginBottom: 12, background: override ? "#e8f5e9" : "#fdecea",
                   border: `1px solid ${override ? "#a5d6a7" : "#f3c7c2"}`,
                   display: "flex", gap: 10, alignItems: "flex-start" }}>
          {override ? <ShieldCheck size={16} style={{ color: "#2e7d32", marginTop: 1 }} />
                    : <Lock size={16} style={{ color: "var(--red)", marginTop: 1 }} />}
          <div style={{ flex: 1, fontSize: 12.5 }}>
            <div className="t-strong">
              {override ? "Manager override active" :
                item.certificate_number
                  ? `Locked — certificate ${item.certificate_number} already issued`
                  : `Locked — line is ${item.status}`}
            </div>
            <div className="t-mut" style={{ marginTop: 2 }}>
              {override
                ? "You are editing a locked line. Change is audit-logged and may invalidate traceability."
                : "Identity & spec fields are immutable for ISO/IEC 17025 traceability. Only a manager can override."}
            </div>
          </div>
          {isManager && (
            <button className={`btn sm ${override ? "ghost" : "gold"}`}
              onClick={() => setOverride((v) => !v)}
              data-testid="edit-line-override-toggle">
              {override ? "Disable override" : "Override lock"}
            </button>
          )}
        </div>
      )}

      <div className="form-grid">
        <div className="fld">
          <label>Description</label>
          <input className="input" value={form.description} disabled={disabled}
            onChange={(e) => set({ description: e.target.value })}
            data-testid="edit-line-desc" />
        </div>
        <div className="fld">
          <label>Manufacturer</label>
          <input className="input" value={form.manufacturer} disabled={disabled}
            onChange={(e) => set({ manufacturer: e.target.value })} />
        </div>
        <div className="fld">
          <label>Serial number</label>
          <input className="input mono" value={form.serial_number} disabled={disabled}
            onChange={(e) => set({ serial_number: e.target.value })}
            data-testid="edit-line-serial" />
        </div>
        <div className="fld">
          <label>Part number</label>
          <input className="input mono" value={form.part_number} disabled={disabled}
            onChange={(e) => set({ part_number: e.target.value })} />
        </div>
        <div className="fld">
          <label>Issue number</label>
          <IssueNumberTypeahead value={form.issue_number} disabled={disabled}
            onChange={(v) => set({ issue_number: v })}
            onPick={(m) => {
              if (disabled) return;
              set({
                description: form.description || m.description || form.description,
                serial_number: form.serial_number || m.serial_number || form.serial_number,
                part_number: form.part_number || m.part_number || form.part_number,
                manufacturer: form.manufacturer || m.manufacturer || form.manufacturer,
                issue_number: m.issue_number || form.issue_number,
              });
            }}
            placeholder="Unique ID when there is no serial number"
            testid="edit-line-issue" />
        </div>
        <div className="fld">
          <label>Discipline</label>
          <select className="input" value={form.discipline} disabled={disabled}
            onChange={(e) => set({ discipline: e.target.value })}>
            {DISCIPLINES.map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div className="fld">
          <label>Owner of Tool</label>
          <input className="input" value={form.location} disabled={disabled}
            onChange={(e) => set({ location: e.target.value })} />
        </div>
        <div className="fld">
          <label>Cal. period</label>
          <select className="input mono" value={form.calibration_period_days || ""} disabled={disabled}
            onChange={(e) => set({ calibration_period_days: e.target.value })}>
            <option value="">— select —</option>
            {![180, 365, 730, 1095, 1460, 1825, "180", "365", "730", "1095", "1460", "1825", ""].includes(form.calibration_period_days) && (
              <option value={form.calibration_period_days}>{form.calibration_period_days} days (current)</option>
            )}
            <option value="180">180 days (6 months)</option>
            <option value="365">365 days (1 year)</option>
            <option value="730">730 days (2 years)</option>
            <option value="1095">1095 days (3 years)</option>
            <option value="1460">1460 days (4 years)</option>
            <option value="1825">1825 days (5 years)</option>
          </select>
        </div>
        <div className="fld">
          <label>SANAS / Traceable</label>
          <select className="input" value={form.cal_method} disabled={disabled}
            onChange={(e) => set({ cal_method: e.target.value })}>
            <option value="traceable">Traceable</option>
            <option value="sanas">SANAS Accredited</option>
          </select>
        </div>
        <div className="fld">
          <label>Priority</label>
          <select className="input" value={form.priority}
            onChange={(e) => set({ priority: e.target.value })}>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
        <div className="fld">
          <label>Technician</label>
          <select className="input" value={form.technician_id}
            onChange={(e) => set({ technician_id: e.target.value })}>
            <option value="">— unassigned —</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Remarks</label>
          <input className="input" value={form.remarks}
            onChange={(e) => set({ remarks: e.target.value })} />
        </div>
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Notes</label>
          <textarea className="input" rows={2} value={form.notes} disabled={disabled}
            onChange={(e) => set({ notes: e.target.value })} />
        </div>
        <div className="fld" style={{ gridColumn: "span 2", display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13 }}>
            <input type="checkbox" checked={form.group_item}
              onChange={(e) => set({ group_item: e.target.checked, group_number: e.target.checked ? form.group_number : "" })}
              data-testid="edit-line-group-toggle" /> Part of a group
          </label>
          {form.group_item && (
            <input className="input mono" style={{ width: 130 }} value={form.group_number}
              onChange={(e) => set({ group_number: e.target.value })}
              placeholder="Group number e.g. BK309"
              data-testid="edit-line-group-number" />
          )}
        </div>
        {isManager && (
          <div className="fld" style={{ gridColumn: "span 2" }}>
            <label>Certificate / CR # (reserved)</label>
            {item.certificate_number ? (
              <input className="input mono" value={item.certificate_number} disabled
                data-testid="edit-line-cert-number" />
            ) : (
              <input className="input mono" value={form.reserved_certificate_number}
                placeholder="e.g. CR26-12345 — manual correction (audit-logged)"
                onChange={(e) => set({ reserved_certificate_number: e.target.value })}
                data-testid="edit-line-cert-number" />
            )}
            <span className="t-mut" style={{ fontSize: 11, marginTop: 2 }}>
              {item.certificate_number
                ? "Certificate already issued — read-only."
                : "Manager-only. Use this to correct/enter a Caspio CR number that was missed on import."}
            </span>
          </div>
        )}
      </div>
      {!locked && <div className="notice" style={{ marginTop: 12 }}>
        <AlertTriangle size={14} />
        <span>Identity fields auto-mirror to the equipment register so the master record stays in sync.</span>
      </div>}
    </Modal>
  );
}
